/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { all, call, CallEffect, select } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  Asset,
  Channel,
  FileFormat,
  SubscriptionsApi,
  Program,
  AssetMediaType,
  Folder,
  FileFormatToFileTypeMap,
  Constants,
  AssetsApi,
  ParentalControlSettings,
  HideUnsubscribed,
  Ducks,
} from '@wit/vtv-x-foundation-lib';
import { Selectors as ChannelsSelectors } from '../epg/channels';
import { SagaFunctions as FoldersSagaFunctions } from '../folders';
import AppConfig from '../../../../services/AppConfig';

function* getSubscriptionIdsForMediaFiles(assets: Asset[]): SagaIterator<string[]> {
  const tasks: CallEffect[] = [];

  assets.forEach((asset) => {
    asset.streams
      .filter((stream) => stream.fileFormat !== FileFormat.TRAILER)
      .forEach((stream) => {
        tasks.push(Ducks.Api.callApi(SubscriptionsApi.getSubscriptionIds, stream.id));
      });
  });

  const subscriptionIdsByFile: string[][] = yield all(tasks);

  return subscriptionIdsByFile.reduce((results, fileSubscriptionIds) => [...results, ...fileSubscriptionIds], []);
}

function* getSubscriptionIdsForRelatedChannelAssets(assets: Asset[]): SagaIterator<string[]> {
  const channels: Channel[] = yield select(ChannelsSelectors.getChannels);

  const subscripionsIds: string[] = [];
  let relatedChannel;
  assets.forEach((asset) => {
    relatedChannel = channels.find(
      (channel) => channel.epgId === (asset as Program).channelEpgId && channel.subscriptionId
    );
    if (relatedChannel) {
      subscripionsIds.push(relatedChannel.subscriptionId as string);
    }
  });

  return subscripionsIds;
}

function* getRelatedSubscriptionsIds(clones: Asset[]): SagaIterator<string[]> {
  const channelsRelatedAssets: Asset[] = [];
  const otherMediaAssets: Asset[] = [];

  clones.forEach((clone) => {
    if (clone.mediaType === AssetMediaType.EPG || clone.mediaType === AssetMediaType.RECORDING) {
      channelsRelatedAssets.push(clone);
    } else {
      otherMediaAssets.push(clone);
    }
  });

  const [otherIds, channelsIds] = yield all([
    call(getSubscriptionIdsForMediaFiles, otherMediaAssets),
    call(getSubscriptionIdsForRelatedChannelAssets, channelsRelatedAssets),
  ]);

  const allIds = [...otherIds, ...channelsIds]; // join all ids in just one array

  return [...new Set(allIds)]; // return only distinct values
}

function* getRelatedSubscriptionsFolders(clones: Asset[]): SagaIterator<Folder[]> {
  // clones are: AssetMediaType.EPISODE, AssetMediaType.MOVIE, AssetMediaType.RECORDING and AssetMediaType.EPG
  const uniqueSubscriptionIds = yield call(getRelatedSubscriptionsIds, clones);
  return yield call(FoldersSagaFunctions.getSubscriptionsFolders, uniqueSubscriptionIds);
}

function* getAssetsByChannel(channelId: number): SagaIterator<Asset[]> {
  const filesFormat: FileFormatToFileTypeMap = yield select(Ducks.Dms.Selectors.getFilesFormat);
  const parentalControlSettings: ParentalControlSettings = yield select(Ducks.Dms.Selectors.getParentalControl);

  return yield Ducks.Pagination.callPaginatedApi(
    `asset_list_${channelId}`,
    0,
    Constants.ASSETS_NUMBER_RELATED,
    Constants.ASSETS_NUMBER_RELATED,
    AssetsApi.assetList,
    channelId,
    filesFormat,
    parentalControlSettings,
    HideUnsubscribed[AppConfig.env.CHANNELS_UNSUBSCRIBED_VISIBILITY]
  );
}

export default {
  getRelatedSubscriptionsFolders,
  getAssetsByChannel,
};
